// src/utils/dbmApi.js
// Shared authenticated client for /api/db_manager.php.
//
// Token lifecycle (the durable fix for the 2026-07-23 Stasney H-10 failure):
//   - sessionStorage("og_access_token") is only a warm cache, seeded at
//     interactive sign-in. It has a 10-minute TTL and is per-tab.
//   - Whenever it is missing (new tab), near expiry (idle tab), or rejected by
//     the server (401), the client transparently REISSUES it from the
//     still-authenticated PHP session via POST /api/token_refresh.php.
//   - Exactly ONE refresh-and-retry per call — never a retry loop.
//   - Only when the server session itself is gone does the user see a
//     truthful "sign in again" error (DbmError.code === "session_expired").
//
// Error contract (DbmError.code):
//   session_expired   – no valid sign-in session; user must log in again
//   no_token_scope    – signed in, but account has no access to this feature
//   account_disabled  – signed in, but account is disabled
//   company_forbidden – authenticated, but tried another company's data
//   scope_denied /
//   forbidden         – authenticated, but not allowed to do that action
//   network           – request never reached the server
//   server_error      – server answered, but with a failure
// Never logs or throws the token value itself.

import { softValidateToken } from "./tokenUtils";

const TOKEN_KEY = "og_access_token";
const API_BASE = "/api/db_manager.php";
const REFRESH_URL = "/api/token_refresh.php";
const AUDIENCE = "ogsupport-db-manager";
const EXP_MARGIN_S = 30; // refresh this many seconds BEFORE actual expiry

export class DbmError extends Error {
  constructor(message, props = {}) {
    super(message);
    this.name = "DbmError";
    this.code = props.code || "server_error";
    this.status = props.status ?? null;
    this.isAuth = !!props.isAuth;
    this.data = props.data ?? null;
  }
}

/** Return the stored token only if it is structurally valid and not about to expire. */
function storedFreshToken() {
  let tok = "";
  try {
    tok = sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    tok = "";
  }
  if (!tok) return null;
  const v = softValidateToken(tok, { audience: AUDIENCE, leeway: 0 });
  if (!v.ok) return null;
  const exp = v.claims && typeof v.claims.exp === "number" ? v.claims.exp : 0;
  if (!exp || exp - Math.floor(Date.now() / 1000) < EXP_MARGIN_S) return null;
  return tok;
}

let refreshInFlight = null;

/**
 * The OgPumper header "Dashboard" link stores its ?token=… as og_token
 * (App.js TokenOrCredsGate). Company admins arriving that way have NO house
 * PHP session, so the refresh endpoint accepts this link token as the
 * identity proof instead (verified + admin/enablement re-checked server-side).
 */
function storedLinkToken() {
  try {
    return sessionStorage.getItem("og_token") || "";
  } catch {
    return "";
  }
}

/** Reissue the access token from the authenticated PHP session OR the
 *  dashboard link token (single-flight). */
async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const linkToken = storedLinkToken();
    let resp;
    try {
      resp = await fetch(REFRESH_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(linkToken ? { link_token: linkToken } : {}),
      });
    } catch {
      throw new DbmError(
        "The sign-in service could not be reached. Check your connection and try again.",
        { code: "network" }
      );
    }
    const data = await resp.json().catch(() => ({}));
    const access = data && data.frontend_access;
    if (resp.ok && data && data.success && access && access.access_token) {
      try {
        sessionStorage.setItem(TOKEN_KEY, access.access_token);
      } catch {
        /* storage unavailable — token still returned for this call */
      }
      return access.access_token;
    }
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    const code = (data && data.error) || (resp.status === 401 ? "session_expired" : "server_error");
    if (code === "session_expired") {
      // Prefer the server's message — it knows whether this was a house
      // session or an expired dashboard link, and says the right next step.
      throw new DbmError(
        (data && data.message) ||
          (linkToken
            ? "Your dashboard link has expired. Go back to your company website and click Admin → Dashboard again."
            : "Your sign-in session has expired. Please log out and sign back in, then reopen this tab."),
        { code, status: resp.status, isAuth: true }
      );
    }
    if (code === "no_token_scope" || code === "account_disabled") {
      throw new DbmError(
        (data && data.message) || "Your account does not have access to this feature.",
        {
          code,
          status: resp.status,
          isAuth: true,
        }
      );
    }
    throw new DbmError(
      (data && data.message) || `Sign-in service error (HTTP ${resp.status}).`,
      { code: "server_error", status: resp.status }
    );
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Return a usable access token: the stored one when still fresh, otherwise a
 * session-driven reissue. force=true always reissues (used after a 401).
 */
export async function ensureAccessToken(force = false) {
  if (!force) {
    const t = storedFreshToken();
    if (t) return t;
  }
  return refreshAccessToken();
}

/**
 * POST an action to /api/db_manager.php with a valid Bearer.
 * One controlled refresh-and-retry on 401 token_missing/token_invalid.
 */
export async function dbmCall(action, payload = {}, _retried = false) {
  const tok = await ensureAccessToken(_retried);
  let resp;
  try {
    resp = await fetch(API_BASE, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tok}`,
      },
      body: JSON.stringify({ action, ...payload }),
    });
  } catch {
    throw new DbmError(
      "The H-10 setup service could not be reached. Check your connection and try again.",
      { code: "network" }
    );
  }
  const data = await resp.json().catch(() => null);
  if (resp.ok && data && data.ok) return data;

  // Structured code: house proxy uses `error`; console-relayed payloads put
  // machine codes (e.g. company_forbidden, company_not_enabled) in `errors[]`.
  let errCode = (data && data.error) || "";
  if (!errCode && data && Array.isArray(data.errors)) {
    const c = data.errors.find((x) => typeof x === "string" && /^[a-z][a-z0-9_]*$/.test(x));
    if (c) errCode = c;
  }
  const authFail = resp.status === 401 || errCode === "token_missing" || errCode === "token_invalid";
  if (authFail && !_retried) {
    // The server rejected our token: reissue once from the session and retry.
    return dbmCall(action, payload, true);
  }
  if (authFail) {
    throw new DbmError(
      "Your sign-in session has expired. Please log out and sign back in, then reopen this tab.",
      { code: "session_expired", status: resp.status, isAuth: true }
    );
  }
  if (resp.status === 403) {
    throw new DbmError((data && data.message) || "Access denied.", {
      code: errCode || "forbidden",
      status: 403,
      data,
    });
  }
  if (!data) {
    throw new DbmError(
      `Unexpected response from the H-10 setup service (HTTP ${resp.status}).`,
      { code: "server_error", status: resp.status }
    );
  }
  throw new DbmError(data.message || `HTTP ${resp.status}`, {
    code: errCode || "server_error",
    status: resp.status,
    data,
  });
}
